/**
 * MSAL configuration for Entra ID SSO.
 *
 * Two env vars are read at build time:
 *   VITE_ENTRA_TENANT_ID  — the Entra tenant we authenticate against
 *   VITE_ENTRA_CLIENT_ID  — the App Registration's Application (client) ID
 *
 * Redirect URI is the current origin + `/auth/callback` (handled by msal-react).
 */

import { PublicClientApplication, LogLevel } from "@azure/msal-browser";

const tenantId = import.meta.env.VITE_ENTRA_TENANT_ID as string | undefined;
const clientId = import.meta.env.VITE_ENTRA_CLIENT_ID as string | undefined;

if (!tenantId || !clientId) {
  // Don't crash here — let App render a friendly "config missing" message.
  console.warn("MSAL: VITE_ENTRA_TENANT_ID / VITE_ENTRA_CLIENT_ID not set; auth disabled.");
}

export const msalConfig = {
  auth: {
    clientId: clientId || "00000000-0000-0000-0000-000000000000",
    authority: `https://login.microsoftonline.com/${tenantId || "common"}`,
    redirectUri: typeof window !== "undefined" ? window.location.origin : "/",
    postLogoutRedirectUri: typeof window !== "undefined" ? window.location.origin : "/",
  },
  cache: { cacheLocation: "sessionStorage" as const, storeAuthStateInCookie: false },
  system: {
    loggerOptions: {
      loggerCallback: (level: LogLevel, message: string) => {
        if (level === LogLevel.Error) console.error("[msal]", message);
      },
      logLevel: LogLevel.Warning,
    },
  },
};

export const loginRequest = {
  // Default scopes — backend API audience is requested as a separate scope
  // when minting an API token via acquireTokenSilent.
  scopes: ["openid", "profile", "email"],
};

export const apiTokenRequest = {
  // The audience of our backend API. Must match ENTRA_AUDIENCE on backend.
  scopes: clientId ? [`api://${clientId}/access_as_user`] : [],
};

export const msalInstance = new PublicClientApplication(msalConfig);
